import { useCallback, useEffect, useSyncExternalStore } from "react";
import { apiFetch } from "@shared/lib/api";
import type { DashboardPreferencesResponse, NavLayout } from "@shared/lib/types";

const STORAGE_KEY = "nav_layout";
const DEFAULT_LAYOUT: NavLayout = "islands";

function getCached(): NavLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "islands" || raw === "sidebar") return raw;
  } catch {
    // ignore
  }
  return null;
}

function setCache(value: NavLayout) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

// Shared module-level store so every consumer of useNavLayout (the layout
// selector in the user menu, AdminShell, DashboardPage, …) re-renders the
// instant the layout changes — no manual page refresh required.
let currentLayout: NavLayout = getCached() ?? DEFAULT_LAYOUT;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setStore(next: NavLayout) {
  if (next === currentLayout) return;
  currentLayout = next;
  setCache(next);
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): NavLayout {
  return currentLayout;
}

// Keep other tabs in sync when the preference changes.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    const next = e.newValue;
    if (next === "islands" || next === "sidebar") {
      if (next !== currentLayout) {
        currentLayout = next;
        emit();
      }
    }
  });
}

// Fetch the server-side preference once per app session, not per consumer.
let serverSyncStarted = false;

export function useNavLayout() {
  const layout = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (serverSyncStarted) return;
    serverSyncStarted = true;
    (async () => {
      try {
        const res = await apiFetch<DashboardPreferencesResponse>(
          "/api/v1/me/dashboard_preferences",
        );
        const server = res.data.dashboard_preferences.nav_layout;
        if (server === "islands" || server === "sidebar") {
          setStore(server);
        }
      } catch {
        // keep cached / default
      }
    })();
  }, []);

  const setLayout = useCallback(async (next: NavLayout) => {
    const previous = currentLayout;
    setStore(next);
    try {
      await apiFetch<DashboardPreferencesResponse>(
        "/api/v1/me/dashboard_preferences",
        {
          method: "PUT",
          body: { dashboard_preferences: { nav_layout: next } },
        },
      );
    } catch {
      setStore(previous);
    }
  }, []);

  return { layout, setLayout };
}
